import { GalleryItem, IProduct } from '@topshots/core-models';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { Button } from '../button/button';
import { ButtonTier } from '../button/button.types';
import { Currency } from '../currency/currency';
import { close, ModalContent, open } from '../modal/modalSlice';
import styles from './add-to-cart-modal.module.css';
import { addToCart, selectProductQuantity } from './cart.slice';
import { MontageGrid } from './montage-grid';
import { cancel, selectMontage } from './montage.slice';
import { useGetProductAddOnsQuery } from '../app/api/products';
import { FormRenderer } from './form-renderer';
import { Quant2 } from '../quant/quant2';
import { Formik, FormikConfig } from 'formik';
import { CartItem } from './cart.types';
import { FormGroup } from '../forms/form-group';

export const AddToCartModal = ({
  product,
  images,
  albumId,
  eventId,
}: {
  product: IProduct;
  images: GalleryItem[];
  albumId: string;
  eventId: string;
}) => {
  const { data: addOns } = useGetProductAddOnsQuery(product._id.toString());
  const montage = useAppSelector(selectMontage);
  const [multiData, setMultiData] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const cartQuant = useAppSelector(
    selectProductQuantity(product._id.toString())
  );
  let unitPrice = {
    discounted: false,
    from: product?.price ?? 100,
    to: product?.price ?? 100,
  };
  switch (product?.pricingModel) {
    case 'tiered':
      // eslint-disable-next-line no-case-declarations
      const tier = product.priceTiers.find(
        (tp) =>
          tp.minVolume <= cartQuant + 1 &&
          (tp.maxVolume == null || tp.maxVolume >= cartQuant)
      );
      unitPrice = {
        discounted: tier?.price !== product.priceTiers[0].price,
        from: product.priceTiers[0].price,
        to: tier?.price ?? product.price,
      };
      break;
  }

  const formikConfig: FormikConfig<Partial<CartItem>> = {
    initialValues: {
      quantity: 1,
      addOnIds: [],
      productFormData: [],
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      dispatch(
        addToCart([
          {
            productId: product._id.toString(),
            quantity: values.quantity ?? 1,
            albumId: albumId,
            eventId: eventId,
            imageIds: images.map((i) => i.id),
            addOnIds: values.addOnIds ?? [],
            productFormData: values.productFormData,
          },
        ])
      );
      if (montage.enabled) {
        dispatch(cancel());
      }
      dispatch(open({ title: 'Your cart', contentKey: ModalContent.Cart }));
    },
  };
  return (
    <Formik {...formikConfig}>
      {({ getFieldProps, setFieldValue, values, handleSubmit }) => (
        <form onSubmit={handleSubmit} className={styles.item}>
          {images.length > 1 ? (
            <MontageGrid
              className={styles.imageGrid}
              requiredItems={images.length}
              items={images}
            />
          ) : images.length > 0 ? (
            <img
              alt={images[0].title}
              className={styles.image}
              src={images[0].imageUrl}
            />
          ) : null}

          <span className={styles.name}>{product.name}</span>

          {/* <Currency value={product.price} className={styles.price} /> */}
          <Currency
            value={unitPrice.discounted ? unitPrice.to : unitPrice.from}
            className={styles.price}
          />
          <Quant2 min={1} {...getFieldProps('quantity')} />

          <div className={styles.addOns}>
            {addOns && addOns.length > 0 && (
              <>
                <h4 className={styles.addOnsTitle}>Upgrade your order</h4>
                <ul className={styles.addOnsList}>
                  {addOns.map((a) => (
                    <li key={a._id.toString()} className={styles.addOn}>
                      <input
                        id={a._id.toString()}
                        defaultChecked={false}
                        type="checkbox"
                        onChange={(evt) => {
                          if (evt.target.checked) {
                            setFieldValue('addOnIds', [
                              ...(values.addOnIds ?? []),
                              a._id,
                            ]);
                            // setSelectedAddOns([...selectedAddOns, a]);
                          } else {
                            // setSelectedAddOns(
                            //   selectedAddOns.filter((s) => s._id !== a._id)
                            // );
                            setFieldValue(
                              'addOnIds',
                              (values.addOnIds ?? []).filter(
                                (id) => id !== a._id.toString()
                              )
                            );
                          }
                        }}
                      />
                      <label htmlFor={a._id.toString()}>{a.name}</label>
                      <Currency value={a.price} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {(product?.formSchema?.length ?? 0) > 0 && (
            <div className={styles.formData}>
              {(values.quantity ?? 1) > 1 && (
                <FormGroup>
                  <input
                    checked={!multiData}
                    onChange={(evt) => {
                      setMultiData((prev) => !prev);
                      setFieldValue('productFormData', [
                        values.productFormData?.[0] ?? {},
                      ]);
                    }}
                    type="checkbox"
                    id="formData"
                  />
                  <label htmlFor="formData">Use the same details for all</label>
                </FormGroup>
              )}
              {Array.from({ length: multiData ? values.quantity ?? 1 : 1 }).map(
                (_, index) => (
                  <FormRenderer
                    key={index}
                    formikPath={(field) => {
                      return `productFormData[${index}].${field.id}`;
                    }}
                    fields={product?.formSchema ?? []}
                    values={{
                      address: '',
                      phone: '',
                      email: '',
                    }}
                    onChange={(fieldId, value) => {
                      return;
                    }}
                  />
                )
              )}
            </div>
          )}
          <div className={styles.buttons}>
            <Button
              tier={ButtonTier.Secondary}
              className={styles.cancelButton}
              onClick={() => {
                dispatch(close());
              }}
            >
              Cancel
            </Button>
            <Button type="submit" className={styles.addButton}>
              Add to cart
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};
